import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";

/**
 * Records an audit log entry for changes to wrestlers or matches
 * @param {string} entityType - "wrestler" or "match"
 * @param {string} entityId - ID of the wrestler or match
 * @param {string} action - "create", "update", or "delete"
 * @param {object} changes - Object with changed fields: { fieldName: { old: oldValue, new: newValue } }
 * @param {string} userId - ID of the user making the change (optional, defaults to "system")
 * @param {string} userName - Name of the user making the change (optional)
 */
export async function logAuditEntry(
  entityType,
  entityId,
  action,
  changes,
  userId = "system",
  userName = "System"
) {
  try {
    const db = getFirestore();
    const auditLogsRef = collection(db, "auditLogs");

    await addDoc(auditLogsRef, {
      entityType: entityType, // "wrestler" or "match"
      entityId: entityId,
      action: action, // "create", "update", "delete"
      changes: changes, // Object with field changes
      userId: userId,
      userName: userName,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    });
  } catch (_error) {
    // Don't throw - audit logging failure shouldn't break the main operation
  }
}

/**
 * Creates a changes object by comparing old and new values
 * @param {object} oldValues - Previous values
 * @param {object} newValues - New values
 * @param {array} fieldsToTrack - Array of field names to compare
 * @returns {object} Changes object with { fieldName: { old, new } }
 */
export function computeChanges(oldValues, newValues, fieldsToTrack) {
  const changes = {};

  fieldsToTrack.forEach((field) => {
    const oldVal = oldValues[field];
    const newVal = newValues[field];

    // Record change if values differ (handle null/undefined as equivalent)
    if (oldVal !== newVal) {
      changes[field] = {
        old: oldVal || null,
        new: newVal || null,
      };
    }
  });

  return changes;
}

/**
 * Log a new wrestler being created
 */
export async function logWrestlerCreate(
  wrestlerData,
  userId = "system",
  userName = "System"
) {
  const changes = {};
  const fieldsToLog = [
    "FirstName",
    "LastName",
    "FullName",
    "School",
    "Grade",
    "RankLeague",
    "RankWeightClass",
    "Skill",
    "Wins",
    "Losses",
  ];

  fieldsToLog.forEach((field) => {
    if (wrestlerData[field] !== undefined) {
      changes[field] = {
        old: null,
        new: wrestlerData[field],
      };
    }
  });

  // Note: entityId will be added after document creation
  return changes;
}

/**
 * Log a wrestler being updated
 */
export async function logWrestlerUpdate(
  wrestlerId,
  oldData,
  newData,
  userId = "system",
  userName = "System"
) {
  const fieldsToTrack = [
    "FirstName",
    "LastName",
    "FullName",
    "School",
    "Grade",
    "RankLeague",
    "RankWeightClass",
    "Skill",
    "Wins",
    "Losses",
  ];
  const changes = computeChanges(oldData, newData, fieldsToTrack);

  if (Object.keys(changes).length > 0) {
    await logAuditEntry(
      "wrestler",
      wrestlerId,
      "update",
      changes,
      userId,
      userName
    );
  }
}

/**
 * Log a new match being created
 */
export async function logMatchCreate(
  wrestler1Id,
  wrestler2Id,
  matchData,
  userId = "system",
  userName = "System"
) {
  const changes = {};
  const fieldsToLog = [
    "Location",
    "MatchDate",
    "Season",
    "OpponentFullName",
    "Result",
    "Type",
    "VideoLink",
  ];

  fieldsToLog.forEach((field) => {
    if (matchData[field] !== undefined) {
      changes[field] = {
        old: null,
        new: matchData[field],
      };
    }
  });

  // Log for both wrestlers
  const matchDescription = `${matchData.OpponentFullName || "Unknown"} - ${
    matchData.Type || "Unknown"
  } (${matchData.MatchDate || "Unknown"})`;
  return { changes, matchDescription };
}

/**
 * Log a match being updated
 */
export async function logMatchUpdate(
  matchId,
  wrestler1Id,
  oldData,
  newData,
  userId = "system",
  userName = "System"
) {
  const fieldsToTrack = [
    "Location",
    "MatchDate",
    "Season",
    "OpponentFullName",
    "Result",
    "Type",
    "VideoLink",
    "WrestlerID",
  ];
  const changes = computeChanges(oldData, newData, fieldsToTrack);

  if (Object.keys(changes).length > 0) {
    // Log to the main match collection (if separate from athlete subcollection)
    await logAuditEntry("match", matchId, "update", changes, userId, userName);
  }
}

/**
 * Log a wrestler deletion
 */
export async function logWrestlerDelete(
  wrestlerId,
  wrestlerData,
  userId = "system",
  userName = "System"
) {
  const fieldsToLog = [
    "FirstName",
    "LastName",
    "FullName",
    "School",
    "Grade",
    "RankLeague",
    "RankWeightClass",
    "Skill",
    "Wins",
    "Losses",
  ];
  const changes = {};

  fieldsToLog.forEach((field) => {
    if (wrestlerData[field] !== undefined) {
      changes[field] = {
        old: wrestlerData[field],
        new: null,
      };
    }
  });

  await logAuditEntry(
    "wrestler",
    wrestlerId,
    "delete",
    changes,
    userId,
    userName
  );
}

/**
 * Log a match deletion
 */
export async function logMatchDelete(
  matchId,
  matchData,
  userId = "system",
  userName = "System"
) {
  const fieldsToLog = [
    "Location",
    "MatchDate",
    "Season",
    "OpponentFullName",
    "Result",
    "Type",
    "VideoLink",
  ];
  const changes = {};

  fieldsToLog.forEach((field) => {
    if (matchData[field] !== undefined) {
      changes[field] = {
        old: matchData[field],
        new: null,
      };
    }
  });

  await logAuditEntry("match", matchId, "delete", changes, userId, userName);
}

function pickChangeSide(fieldChange, action) {
  if (!fieldChange || typeof fieldChange !== "object") return null;
  const act = action || "update";
  const hasOld = Object.prototype.hasOwnProperty.call(fieldChange, "old");
  const hasNew = Object.prototype.hasOwnProperty.call(fieldChange, "new");
  if (act === "delete" && hasOld) return fieldChange.old;
  if (act === "create" && hasNew) return fieldChange.new;
  if (hasNew && fieldChange.new != null && fieldChange.new !== "")
    return fieldChange.new;
  if (hasOld && fieldChange.old != null && fieldChange.old !== "")
    return fieldChange.old;
  if (hasNew) return fieldChange.new;
  if (hasOld) return fieldChange.old;
  return null;
}

function stringifyAuditDisplayValue(v) {
  if (v == null || v === "") return "";
  if (typeof v === "object" && typeof v.toDate === "function") {
    try {
      return v.toDate().toLocaleString();
    } catch (_e) {
      return String(v);
    }
  }
  if (typeof v === "object") return String(v);
  return String(v).trim();
}

/**
 * Best-effort wrestler display name from an audit entry's `changes` when the
 * athlete document no longer exists (e.g. after bulk delete).
 */
export function wrestlerDisplayNameFromAuditChanges(changes, action) {
  if (!changes || typeof changes !== "object") return "";
  const act = action || "update";
  const pick = (field) =>
    stringifyAuditDisplayValue(pickChangeSide(changes[field], act));
  const full = pick("FullName");
  if (full) return full;
  const fn = pick("FirstName");
  const ln = pick("LastName");
  return [fn, ln].filter(Boolean).join(" ").trim();
}

/**
 * Short match description from audit `changes` when live docs are gone.
 */
export function matchSummaryFromAuditChanges(changes, action) {
  if (!changes || typeof changes !== "object") return "";
  const act = action || "update";
  const pick = (field) =>
    stringifyAuditDisplayValue(pickChangeSide(changes[field], act));
  const opponent = pick("OpponentFullName") || pick("OpponentName");
  const when = pick("MatchDate");
  const type = pick("Type");
  const result = pick("Result");
  const parts = [];
  if (opponent) parts.push(`vs ${opponent}`);
  if (when) parts.push(when);
  if (type) parts.push(type);
  if (result) parts.push(result);
  return parts.join(" · ");
}

/**
 * Deletes audit log documents for the given entity type and ids (chunks of 10).
 * Requires a composite index on auditLogs: entityType + entityId.
 */
export async function deleteAuditLogsForEntityIds(db, entityType, entityIds) {
  const ids = [...new Set((entityIds || []).filter(Boolean))];
  if (ids.length === 0) return 0;
  const chunkSize = 10;
  let total = 0;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const q = query(
      collection(db, "auditLogs"),
      where("entityType", "==", entityType),
      where("entityId", "in", chunk)
    );
    const snap = await getDocs(q);
    if (snap.empty) continue;
    let batch = writeBatch(db);
    let ops = 0;
    for (let j = 0; j < snap.docs.length; j++) {
      batch.delete(snap.docs[j].ref);
      ops += 1;
      total += 1;
      if (ops >= 500) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0) {
      await batch.commit();
    }
  }
  return total;
}
